import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { useGetPackagesQuery } from "../../../api/endpoints/packageDefinitionsApi";
import { useLanguage } from "../../../contexts/LanguageContext";
import { toastService } from "../../../utils/toastService";

function usePackageDefinitionsData() {
  const { th } = useLanguage();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });

  const createInitialFilters = useCallback(
    () => ({
      packageName: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
      },
    }),
    []
  );

  const [filters, setFilters] = useState(createInitialFilters());

  const queryParams = useMemo(
    () => ({
      search: filters.packageName?.constraints?.[0]?.value || "",
      pageNumber: pagination.PageNumber,
      pageSize: pagination.PageSize,
    }),
    [filters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useGetPackagesQuery(
    queryParams,
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    setLoading(isLoading || isFetching);
  }, [isLoading, isFetching]);

  useEffect(() => {
    setRows(data?.rows ?? data?.data?.rows ?? []);
    setTotalCount(data?.totalCount ?? data?.data?.totalCount ?? 0);
  }, [data]);

  const handleSearch = useCallback(
    (text) => {
      setFilters((prev) => ({
        ...prev,
        packageName: {
          operator: FilterOperator.AND,
          constraints: [
            { value: text || null, matchMode: FilterMatchMode.STARTS_WITH },
          ],
        },
      }));
      setPagination((p) => ({ ...p, PageNumber: 1 }));
      setTimeout(() => refetch(), 50);
    },
    [refetch]
  );

  const handleApplyFilters = useCallback(
    (updated) => {
      if ("packageName" in updated) handleSearch(updated.packageName);
    },
    [handleSearch]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(createInitialFilters());
    setPagination((p) => ({ ...p, PageNumber: 1 }));
    setTimeout(() => refetch(), 50);
  }, [createInitialFilters, refetch]);

  const onPageChange = useCallback((event) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPagination((prev) => ({
      ...prev,
      PageNumber: newPageNumber,
      PageSize: event.rows,
    }));
  }, []);

  const handleEdit = useCallback((row) => {
    document.dispatchEvent(new CustomEvent("pkg:edit", { detail: row }));
  }, []);

  const handleShowDetail = useCallback((row) => {
    document.dispatchEvent(new CustomEvent("pkg:detail", { detail: row }));
  }, []);

  const handleRefresh = useCallback(() => refetch(), [refetch]);

  useEffect(() => {
    if (error) toastService.error(th("common.unexpectedError"));
  }, [error, th]);

  return {
    rows,
    loading,
    totalCount,
    pagination,
    filters,
    filterFields: [
      {
        field: "packageName",
        headerName: th("packageDefinitions.packageName"),
        type: "text",
      },
    ],
    customFilterTypes: { packageName: "text" },
    customFilterOptions: {},
    onPageChange,
    handleApplyFilters,
    handleResetFilters,
    handleSearch,
    handleEdit,
    handleShowDetail,
    handleRefresh,
  };
}

export default usePackageDefinitionsData;
